import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/utils/toast';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { PostCreator } from '@/components/PostCreator';
import { VariationCreator } from '@/components/VariationCreator';
import { usePostCreatorStore } from '@/stores/postCreatorStore';
import { useVariationCreatorStore } from '@/stores/variationCreatorStore';
import { variationsService } from '@/services/variations.service';
import { useInvalidateRelease } from '@/lib/invalidateRelease';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

/**
 * ONE PostCreator driven by postCreatorStore (any page can open it over the current page).
 * Pages that keep their own local PostCreator do not use the store and are unaffected.
 */
export const PostComposerHost = () => {
  const isOpen = usePostCreatorStore((s) => s.isOpen);
  const initialCaption = usePostCreatorStore((s) => s.initialCaption);
  const editingPostId = usePostCreatorStore((s) => s.editingPostId);
  const initialMedia = usePostCreatorStore((s) => s.initialMedia);
  const initialScheduleOpen = usePostCreatorStore((s) => s.initialScheduleOpen);
  const close = usePostCreatorStore((s) => s.close);
  const invalidateRelease = useInvalidateRelease();

  return (
    <PostCreator
      isOpen={isOpen}
      onClose={close}
      initialCaption={initialCaption}
      editingPostId={editingPostId}
      initialMedia={initialMedia}
      initialScheduleOpen={initialScheduleOpen}
      onSaveSuccess={() => {
        invalidateRelease();
      }}
    />
  );
};

/**
 * ONE VariationCreator driven by variationCreatorStore. Loads the variation in the exact
 * editor shape (same as opening it from the Variations table); the creator loads its parent
 * post itself from variation.postId. With no variationId it opens in create mode for the post.
 * The inner component only exists while open, so every open is a fresh mount + fresh fetch
 * (VariationCreator reads the variation once per id; it must never be fed a cached row).
 */
export const VariationComposerHost = () => {
  const isOpen = useVariationCreatorStore((s) => s.isOpen);
  const postId = useVariationCreatorStore((s) => s.postId);
  if (!isOpen || postId === undefined) return null;
  return <VariationComposerInner postId={postId} />;
};

const VariationComposerInner = ({ postId }: { postId: number }) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const variationId = useVariationCreatorStore((s) => s.variationId);
  const initialScheduleOpen = useVariationCreatorStore((s) => s.initialScheduleOpen);
  const storeClose = useVariationCreatorStore((s) => s.close);
  const invalidateRelease = useInvalidateRelease();

  const isEdit = variationId !== undefined;
  const queryKey = ['variation-editor', postId, variationId];
  const { data: variation, isError, isFetchedAfterMount } = useQuery({
    queryKey,
    queryFn: () => variationsService.getForEditor(postId, variationId as number),
    enabled: isEdit,
    staleTime: 0,
    gcTime: 0,
  });

  const close = useCallback((): void => {
    storeClose();
    queryClient.removeQueries({ queryKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeClose, queryClient, postId, variationId]);

  // A failed load closes the host and tells the user (same message as the deep link).
  useEffect(() => {
    if (isError) {
      toast.error(t('variation_link_not_found'));
      close();
    }
  }, [isError, close, t]);

  // Edit mode: only mount from a fetch made by THIS open, never from cache.
  if (isEdit && !(variation && isFetchedAfterMount)) {
    // Minimal loading modal so a slow load is never invisible, with a way out (a failed load closes via the effect above).
    return (
      <Modal open onOpenChange={(next) => !next && close()} size="sm" showCloseButton={false}>
        <div className="flex flex-col items-center gap-4 p-6 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
          <p className="text-sm text-gray-500">{t('loading', { defaultValue: 'Loading...' })}</p>
          <Button variant="secondary" size="sm" className="max-sm:min-h-[44px] max-sm:min-w-[44px]" onClick={close}>
            {t('close', { defaultValue: 'Close' })}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <VariationCreator
      key={`${postId}-${variationId ?? 'new'}`}
      isOpen
      onClose={close}
      variation={variation}
      preSelectedPostId={isEdit ? undefined : postId}
      initialScheduleOpen={initialScheduleOpen}
      onSuccess={() => {
        invalidateRelease();
      }}
    />
  );
};

/**
 * Both composer stores survive route changes on purpose, but never a brand switch: a composer opened for brand A
 * must not be able to save after the effective team became brand B. Same signal as ReleaseDrawerHost.
 */
const useCloseComposersOnTeamChange = (): void => {
  const { teamId } = useEffectiveTeamId();
  const previousTeamRef = useRef<number | null>(teamId);
  useEffect(() => {
    const previous = previousTeamRef.current;
    previousTeamRef.current = teamId;
    if (previous === null || previous === teamId) return;
    usePostCreatorStore.getState().close();
    useVariationCreatorStore.getState().close();
  }, [teamId]);
};

export const GlobalComposerHost = () => {
  useCloseComposersOnTeamChange();
  return (
    <>
      <PostComposerHost />
      <VariationComposerHost />
    </>
  );
};
